import { useEffect, useId, useState } from 'react'
import type { PinVault } from '@application/ports.ts'
import { childPinKey } from '@application/ports.ts'
import type { Member } from '@domain/state.ts'
import { es } from '../strings/es.ts'
import { PinField } from '../shell/PinField.tsx'

/**
 * Where a parent gives one child their own number, or takes it away again.
 *
 * It says which of the two states it is in before offering the button, because
 * "poner un número" and "cambiar el número" are different promises and a parent
 * setting this up with two children arguing behind them should not have to work
 * out which one they are looking at.
 */
export function ChildPinSetting({
  member,
  vault,
  onChanged,
}: {
  member: Member
  vault: PinVault
  onChanged: () => void
}) {
  const [isSet, setIsSet] = useState<boolean | null>(null)
  const [editing, setEditing] = useState(false)
  const [pin, setPin] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const fieldId = useId()
  const key = childPinKey(member.id)

  useEffect(() => {
    let cancelled = false
    // Si no se puede leer, `isSet` se queda en null y el ajuste no se dibuja.
    // Enseñar «ahora entra sin número» sin saberlo sería mentir sobre una
    // puerta.
    void vault
      .state(key)
      .then((state) => {
        if (!cancelled) setIsSet(state.isSet)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [vault, key])

  const save = async (): Promise<void> => {
    if (!/^\d{4,8}$/.test(pin)) {
      setNote(es.childPin.tooShort)
      return
    }
    await vault.set(pin, key)
    setIsSet(true)
    setEditing(false)
    setPin('')
    setNote(es.childPin.saved)
    onChanged()
  }

  const clear = async (): Promise<void> => {
    await vault.remove(key)
    setIsSet(false)
    setEditing(false)
    setPin('')
    setNote(es.childPin.removed)
    onChanged()
  }

  if (isSet === null) return null

  return (
    <section className="border-rule mt-6 border-t pt-5">
      <h3 className="m-0 mb-1 text-base font-extrabold">{es.childPin.title}</h3>
      <p className="text-muted mt-0 mb-2 text-sm">{es.childPin.help}</p>
      <p className="m-0 mb-3 text-sm font-bold">{isSet ? es.childPin.active : es.childPin.none}</p>

      {editing ? (
        <>
          <PinField id={fieldId} label={es.childPin.enter} value={pin} onChange={setPin} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                void save()
              }}
              className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
            >
              {es.childPin.save}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setPin('')
              }}
              className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
            >
              {es.config.close}
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setNote(null)
              setEditing(true)
            }}
            className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
          >
            {isSet ? es.childPin.change : es.childPin.set}
          </button>
          {isSet ? (
            <button
              type="button"
              onClick={() => {
                void clear()
              }}
              className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
            >
              {es.childPin.clear}
            </button>
          ) : null}
        </div>
      )}

      {note ? (
        <p role="status" className="text-muted m-0 mt-3 text-sm font-bold">
          {note}
        </p>
      ) : null}
    </section>
  )
}
